// ═══════════════════════════════════════════════════════════════════════════
//  ExecControl — the ONE way a destructive / money-moving control is built.
//  KALI mobile audit M-H2 (God D25, 2026-10-04).
//
//  A generalisation of HoldButton (see that file for the hold mechanics):
//   * min 48x48 hit area on every screen — a 14 px KILL chip is a miss-tap.
//   * hold 0.9 s on PAPER, 1.4 s on LIVE (same clock as ExecutionArmBar).
//   * NO window.confirm / window.prompt anywhere on an execution path: iOS
//     renders those as a blocking system sheet and a hold is the confirm.
//   * iOS has no navigator.vibrate, so the commit is acknowledged with a
//     120 ms visual "tick" flash instead.
//   * `compact` keeps the 48 px floor but sizes to content (rows, chips).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HoldButton } from './HoldButton';

export const EXEC_HOLD_PAPER_MS = 900;
export const EXEC_HOLD_LIVE_MS = 1400;

export interface ExecControlProps {
  onConfirm: () => void;
  /** LIVE money: 1.4 s hold instead of 0.9 s. */
  live?: boolean;
  disabled?: boolean;
  tone?: 'pos' | 'neg' | 'info';
  /** Size to content (keeps the 48x48 floor) instead of full width. */
  compact?: boolean;
  holdingLabel?: string;
  title?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const ExecControl: React.FC<ExecControlProps> = ({
  onConfirm, live = false, disabled = false, tone = 'neg', compact = false,
  holdingLabel = 'HOLD', title, style, children,
}) => {
  const [tick, setTick] = useState(false);
  const tRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (tRef.current) clearTimeout(tRef.current); }, []);

  const confirm = useCallback(() => {
    setTick(true);
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setTick(false), 120);
    onConfirm();
  }, [onConfirm]);

  return (
    <span title={title} data-exec-control={live ? 'live' : 'paper'}
      style={{ display: compact ? 'inline-flex' : 'flex', width: compact ? undefined : '100%',
        borderRadius: 'var(--jc-r-md)', outline: tick ? '2px solid var(--jc-pos)' : 'none',
        outlineOffset: 1, transition: 'outline-color 120ms linear' }}>
      <HoldButton
        onConfirm={confirm}
        holdMs={live ? EXEC_HOLD_LIVE_MS : EXEC_HOLD_PAPER_MS}
        disabled={disabled}
        tone={tone}
        holdingLabel={holdingLabel}
        style={{
          minHeight: 48, minWidth: 48,
          width: compact ? 'auto' : '100%',
          padding: compact ? '0 12px' : '0 18px',
          fontSize: 11,
          ...style,
        }}>
        {children}
      </HoldButton>
    </span>
  );
};

export default ExecControl;
